import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import axiosInstance from '../../axios/axiosAuthInterceptor';
import './SignUpSuccessPage.scss';

type StripeSession = {
    id: string;
    payment_status?: 'paid' | 'unpaid' | 'no_payment_required';
    status?: 'open' | 'complete' | 'expired';
    amount_total?: number; // in cents
    currency?: string; // "eur"
    customer_details?: {
        email?: string;
        name?: string;
    };
    subscription?: any; // expanded by backend (optional)
    customer?: any; // expanded by backend (optional)
    // If your backend forwards line items, you can type them here as well
};

const centsTo = (amount?: number, currency?: string) => {
    if (amount == null) return '';
    // simple EUR formatting
    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: (currency || 'EUR').toUpperCase(),
    }).format(amount / 100);
};

const SignupSuccessPage: React.FC = () => {
    const { search } = useLocation();
    const history = useHistory();
    const isLoggedIn = !!localStorage.getItem('accessToken');

    const qp = useMemo(() => new URLSearchParams(search), [search]);
    const sessionId = qp.get('session_id');
    const student = qp.get('student') === '1';

    const [loading, setLoading] = useState<boolean>(!!sessionId && !student);
    const [error, setError] = useState<string | null>(null);
    const [session, setSession] = useState<StripeSession | null>(null);

    useEffect(() => {
        if (student) return;
        if (!sessionId) {
            setError('Missing session id.');
            setLoading(false);
            return;
        }

        const ac = new AbortController();
        (async () => {
            try {
                const res = await axiosInstance.get(`/stripe/session?session_id=${encodeURIComponent(sessionId)}`, {
                    signal: ac.signal,
                });
                if (res.status !== 200) {
                    const msg = res.statusText;
                    throw new Error(msg || 'Failed to fetch session');
                }
                const data = await res.data;
                setSession(data);
            } catch (e: any) {
                console.log(e);
                setError(e.message || 'Failed to verify payment');
            } finally {
                setLoading(false);
            }
        })();

        return () => ac.abort();
    }, [sessionId, student]);

    // Render states
    if (student) {
        return (
            <div className="mainLoginPage">
                <div id="successpage" className="search">
                    <div className="card signup-card">
                        <div className="title">Registratie voltooid 🎉</div>
                        <Link to="/login" className="button button-ready">
                            Ga naar inloggen
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="mainLoginPage">
                <div className="search">
                    <div className="card signup-card">
                        <div className="title">Bevestigen…</div>
                        <p>We halen je betaalstatus op. Een moment geduld.</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !session) {
        return (
            <div className="mainLoginPage">
                <div className="search">
                    <div className="card signup-card">
                        <div className="title">Er ging iets mis</div>
                        <p>{error ?? 'Kon de betaalgegevens niet ophalen.'}</p>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button className="button" onClick={() => history.goBack()}>
                                Probeer opnieuw
                            </button>
                            <Link className="button button-ready" to="/signup">
                                Terug naar aanmelden
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const paid = session.payment_status === 'paid' || session.status === 'complete';
    const amount = centsTo(session.amount_total, session.currency);

    return (
        <div className="mainLoginPage">
            <div id="successpage" className="search">
                <div className="card signup-card">
                    <div className="title">{paid ? 'Betaling voltooid 🎉' : 'Betaling niet afgerond'}</div>

                    {paid ? (
                        <>
                            <p>
                                Dank je wel! Je abonnement is geactiveerd voor{' '}
                                <strong>{session.customer_details?.email ?? 'je e-mailadres'}</strong>.
                            </p>
                            {amount && (
                                <p>
                                    Totaal betaald: <strong>{amount}</strong>
                                </p>
                            )}
                            {isLoggedIn ? (
                                <>
                                    <Link to="/" className="button button-ready">
                                        Begin met zoeken
                                    </Link>
                                </>
                            ) : (
                                <>
                                    <Link to="/login" className="button button-ready">
                                        Ga naar inloggen
                                    </Link>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <p>
                                We konden je betaling niet bevestigen. Als dit onjuist is, vernieuw de pagina of probeer
                                opnieuw.
                            </p>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="button" onClick={() => window.location.reload()}>
                                    Opnieuw controleren
                                </button>
                                <Link to="/signup" className="button button-ready">
                                    Terug naar aanmelden
                                </Link>
                            </div>
                        </>
                    )}

                    {/* <div style={{ marginTop: 12, opacity: 0.8, fontSize: 14 }}>
                        <div>Sessie: {session.id}</div>
                        <div>Status: {session.status ?? 'onbekend'}</div>
                        <div>Betaalstatus: {session.payment_status ?? 'onbekend'}</div>
                    </div> */}
                </div>
            </div>
        </div>
    );
};

export default SignupSuccessPage;
